import Link from 'next/link';
import { IoKeyOutline } from 'react-icons/io5';

import { Badge, Button, Group, Stack } from '@mantine/core';
import useSWR from 'swr';

import { ProjectAccessRequest } from 'models';
import { ProjectAccessHelpers } from 'helpers/ProjectAccessHelpers';
import { ProjectHelpers } from 'helpers/ProjectHelpers';

import { Avatar } from 'components/avatar/Avatar';
import { DashboardPanel } from 'components/dashboard-panel/DashboardPanel';
import { useCurrentTeam } from 'hooks/use-current-team';
import { useProject } from 'hooks/use-project';
import { useUser } from 'hooks/use-user';
import { DateService } from 'services/DateService';
import { ProjectAccessService } from 'services/ProjectAccessService';

import { DashboardActionRow, DashboardActionRowSkeleton, THUMBNAIL_SIZE } from './DashboardActionRow';

const DashboardAccessRequests = () => {
    const team = useCurrentTeam();

    const { data: accessRequests = [] } = useSWR(team ? ProjectAccessHelpers.swr.pendingForTeam(team.id) : null, () =>
        ProjectAccessService.list({ team: team!.id, status: 'pending' }),
    );

    if (!accessRequests.length) {
        return null;
    }

    return (
        <DashboardPanel
            icon={<IoKeyOutline />}
            color="orange"
            title={
                <Group gap={6} wrap="nowrap">
                    Access requests
                    <Badge size="xs" variant="filled" px={4} miw={16} color="red">
                        {accessRequests.length}
                    </Badge>
                </Group>
            }
        >
            <Stack gap={0}>
                {accessRequests.map((accessRequest) => (
                    <PendingAccessRequest key={accessRequest.id} accessRequest={accessRequest} />
                ))}
            </Stack>
        </DashboardPanel>
    );
};

const PendingAccessRequest = ({ accessRequest }: { accessRequest: ProjectAccessRequest }) => {
    const { user } = useUser(accessRequest.createdBy);
    const { project } = useProject(accessRequest.project);

    if (!user) {
        return <DashboardActionRowSkeleton />;
    }

    return (
        <DashboardActionRow
            thumbnail={<Avatar user={accessRequest.createdBy} size={THUMBNAIL_SIZE} />}
            title={user.name ?? user.email}
            description={
                <>
                    Wants access to {project?.name ?? 'a project'} ·{' '}
                    {DateService.formatDistanceToNow(accessRequest.createdAt)}
                </>
            }
            rightSection={
                <Button
                    component={Link}
                    href={`${ProjectHelpers.urls.editor(accessRequest.project)}?action=share`}
                    size="compact-xs"
                    variant="default"
                >
                    Review request
                </Button>
            }
        />
    );
};

export { DashboardAccessRequests };
